import { HERO_CODE_ALPHABET } from "./hero";
import { parseHeroCode } from "./classroom";
// 화상 무대 출처는 **한 곳**에서 — 배포 헤더(`Permissions-Policy`)가 같은 값을 봐야 한다

/**
 * 🏫 **반 코드** — 선생님이 만들고, 아이가 넣고 들어온다.
 *
 * ## 두 가지를 동시에 해야 한다
 * ① 아이가 **부를 수 있어야** 한다. 선생님이 칠판에 적고 아이가 읽는다.
 * ② 남이 **못 찾아야** 한다. 화상 방은 이름만 알면 누구나 들어온다 —
 *    아동 수업이라 이건 안전 문제다.
 *
 * 그래서 **낱말 + 무작위 네 자**로 짠다(`별빛-3F7K`). 낱말은 부르기 쉽고,
 * 뒤 네 자가 자물쇠다. 낱말만으로는 절대 안 된다 — 「1반」·「목요일반」은
 * 몇 번만 찍어도 맞는다.
 */

/** 부르기 쉬운 낱말들 — 이 책 세계 안에서. */
export const CLASS_WORDS = [
  "별빛",
  "달밤",
  "새벽",
  "노을",
  "구름",
  "바람",
  "연꽃",
  "소나무",
  "기와",
  "돌담",
  "등불",
  "종소리",
] as const;

/** 뒤에 붙는 자물쇠 길이. 30글자 × 4자리 ≈ 81만 — 낱말까지 곱하면 천만 단위다. */
export const CLASS_CODE_TAIL = 4;

export function generateClassCode(rand: () => number = Math.random): string {
  const word = CLASS_WORDS[Math.floor(rand() * CLASS_WORDS.length) % CLASS_WORDS.length]!;
  let tail = "";
  for (let i = 0; i < CLASS_CODE_TAIL; i++) {
    tail +=
      HERO_CODE_ALPHABET[
        Math.floor(rand() * HERO_CODE_ALPHABET.length) % HERO_CODE_ALPHABET.length
      ];
  }
  return `${word}-${tail}`;
}

/**
 * 아이가 적어 온 반 코드를 받아 준다.
 *
 * 히어로 코드와 같은 규칙이다 — 공백·소문자는 받아 주되,
 * 🔴 **없는 글자를 짐작해서 고치지는 않는다.** 남의 교실이 열릴 수 있다.
 */
export function parseClassCode(input: string): string | null {
  const raw = (input ?? "").trim().replace(/\s/g, "");
  const m = /^(.+?)[-_]?([A-Za-z0-9]{4})$/.exec(raw);
  if (!m) return null;
  const word = m[1]!;
  const tail = m[2]!.toUpperCase();
  if (!(CLASS_WORDS as readonly string[]).includes(word)) return null;
  for (const ch of tail) if (!HERO_CODE_ALPHABET.includes(ch)) return null;
  return `${word}-${tail}`;
}

/**
 * 🗑 **여기 있던 `classRoomName`·`classStageUrl` 을 걷어냈다**(2026-09-03).
 *
 * 반 코드로 `meet.jit.si` 방 주소를 **자동으로** 만들던 함수들이다. 그 방식은
 * 화면에 끼워 넣는 것이라 **5분 뒤 끊겼고**, 그래서 자동 방을 없앤 게 어제 일이다.
 * 그런데 함수는 남아 있었다 — **아무도 안 부르는데 만드는 법은 아는 코드**.
 *
 * 🔴 이런 자리가 제일 위험하다. 다음 사람이 「이미 있네」 하고 다시 배선한다.
 * 지금 화상 주소는 **운영자·선생님이 넣거나**(어느 서비스든), Jitsi 를 골랐을 때
 * `newJitsiRoom` 이 **길고 무작위인** 이름으로 만든다(짧은 이름은 뚫린다).
 */

/** 아이·보호자에게 보여줄 안내. 화면마다 다시 쓰면 말이 갈린다. */
export const CLASS_CONSENT = {
  title: "보호자와 함께 봐 주세요",
  body: "이 수업은 화상으로 진행돼요. 선생님이 수업을 녹화할 수 있어요.",
  camera: "카메라는 꺼진 채로 들어가요. 내 캐릭터가 대신 자리에 앉아요.",
  hint: "얼굴을 보여주고 싶으면 들어간 뒤에 켜면 돼요.",
} as const;

/* ── 🏠 집에서 이어 하기 ──────────────────────────────── */

/**
 * 주소에 반 코드를 싣는 이름.
 *
 * 🔴 **수업 모드(`?class`)와 다른 이름을 쓴다.** 같은 이름을 쓰면 집에서 종이 QR 로
 * 들어온 아이의 화면에서 굿즈·전시가 통째로 사라진다 — `classModeFromSearch` 가
 * 값이 무엇이든 「켜기」로 읽기 때문이다. 집에서는 **반만 기억하고 수업 모드는 안 켠다.**
 */
export const CLASS_CODE_PARAM = "cls";

/** 주소에서 반 코드를 읽는다. 없거나 모양이 아니면 null. */
export function classCodeFromSearch(search: string): string | null {
  try {
    return parseClassCode(new URLSearchParams(search).get(CLASS_CODE_PARAM) ?? "");
  } catch {
    return null; // 주소가 깨져 있어도 화면은 떠야 한다
  }
}

/**
 * 🏠 **집에서 이어 하는 주소** — 종이 쪽지의 QR 이 가리키는 곳.
 *
 * 코드 하나만 있으면 캐릭터는 돌아오지만 **그림책은 안 돌아온다**(반 코드가 없으면
 * 서버에서 못 찾는다). 그래서 반 코드를 함께 싣는다. 수업이 아니면 코드만 싣는다.
 */
export function resumeUrl(base: string, heroCode: string, classCode?: string | null): string {
  const clean = base.replace(/\/$/, "");
  const cls = classCode ? parseClassCode(classCode) : null;
  const q = cls ? `?${CLASS_CODE_PARAM}=${encodeURIComponent(cls)}` : "";
  return `${clean}/h/${heroCode}${q}`;
}

/* ── 👪 부모에게 보내는 주소 ────────────────────────────── */

/** 아이 코드를 싣는 이름. 반 코드(`cls`)와 짝이다. */
export const BOOK_CODE_PARAM = "code";

/**
 * 👪 **아이가 만든 그림책 한 권을 보는 주소** — 부모가 폰에서 넘겨 본다.
 *
 * ## 왜 필요한가
 * 아이가 만든 그림책이 **종이로만** 집에 갔다. 부모는 예쁘다고 보고 그걸로 끝이다 —
 * 어디서 더 볼 수 있는지, 다음 학기가 있는지, 굿즈로 만들 수 있는지가 아무 데도 안 적혀 있다.
 * 재수강과 굿즈가 시작될 자리가 통째로 비어 있었다.
 *
 * ## 🔴 개인정보는 그대로 0 (불변식 1)
 * 주소에 담기는 것은 **반 코드와 아이 코드**뿐이다 — 지금 QR 과 같은 방식이고,
 * 이름·연락처는 어디에도 없다. 새로 모으는 것도 없다.
 *
 * ## 🔴 `/h` 와 다르다
 * `resumeUrl`(`/h/<코드>`)은 **아이가 이어서 만드는** 길이다 — 캐릭터를 이 기기에 되살린다.
 * 이건 **부모가 읽기만** 하는 길이라 아무것도 안 되살린다. 둘을 한 주소로 합치면
 * 부모 폰에 아이 캐릭터가 저장되고, 그 폰으로 아이 그림책이 고쳐질 수 있다.
 */
export function familyBookUrl(base: string, classCode: string, heroCode: string): string {
  const clean = base.replace(/\/$/, "");
  const cls = parseClassCode(classCode);
  if (!cls || !heroCode) return clean;
  return `${clean}/mybook?${CLASS_CODE_PARAM}=${encodeURIComponent(cls)}&${BOOK_CODE_PARAM}=${encodeURIComponent(heroCode)}`;
}

/**
 * 주소에서 아이 코드를 읽는다. 없거나 코드 모양이 아니면 null.
 *
 * 🔴 **`parseHeroCode` 를 쓴다** — 여기서 정규식을 새로 짜면 헷갈리는 글자(0·1·I·L·O·U)를
 * 뺀 규칙이 한쪽에만 남는다. 아이가 종이에서 옮겨 적는 코드라 그 규칙이 곧 기능이다.
 */
export function bookCodeFromSearch(search: string): string | null {
  try {
    return parseHeroCode(new URLSearchParams(search).get(BOOK_CODE_PARAM) ?? "");
  } catch {
    return null; // 주소가 깨져 있어도 화면은 떠야 한다
  }
}
